import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import MenuGroup from '@atlaskit/menu/menu-group';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { DevDocsBylineConnection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { DevDocsBylineConnectionMenuSection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnectionMenuSection.ts';

import type { UseAtAGlanceResult } from './at-a-glance/useAtAGlance.ts';
import {
	BylineConnectionIconStack,
	type BylineConnectionIconStackProps,
} from './BylineConnectionIconStack.tsx';
import { ConnectionsByLineContextBanner } from './ConnectionsByLineContextBanner.tsx';
import { ConnectionsByLineExpandableLinkSection } from './ConnectionsByLineExpandableLinkSection.tsx';
import { ConnectionsByLineMenuFooter } from './footer/ConnectionsByLineMenuFooter.tsx';
import { ConnectionsByLineMenuHeader, HEADER_HEADING_ID } from './ConnectionsByLineMenuHeader.tsx';
import { ShowMoreButton } from './ShowMoreButton.tsx';
import {
	DEFAULT_VISIBLE_LINK_COUNT,
	getTopConnectionLinks,
} from './utils/getTopConnectionLinks.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';
import { useTwgAnalyticsEvents } from './useTwgAnalyticsEvents.ts';

const MENU_WIDTH = '320px';
const TWG_MENU_BODY_MAX_HEIGHT = '600px';

const i18n = defineMessages({
	viewMoreContext: {
		id: 'byline-connections.menu.view-more-context',
		defaultMessage: 'View more context',
		description:
			'Label for the button at the end of the connections byline menu that opens the Teamwork Graph preview panel for the current page.',
	},
	linksTo: {
		id: 'byline-connections.menu.section.links-to',
		defaultMessage: 'Links to ({count})',
		description:
			'Heading of the connections byline menu links section, followed by the total number of links found on the current page in parentheses.',
	},
	appearsIn: {
		id: 'byline-connections.menu.section.appears-in',
		defaultMessage: 'Appears in ({count})',
		description:
			'Heading of the connections byline menu incoming links section, followed by the total number of other pages and items where the current page appears in parentheses.',
	},
	relatedContent: {
		id: 'byline-connections.menu.section.related-content',
		defaultMessage: 'Related content ({count})',
		description:
			'Heading of the connections byline menu related content section, followed by the number of Confluence pages and items that Teamwork Graph identifies as related to the current page in parentheses.',
	},
});

export type ConnectionGroup = {
	product: string;
	productKey: string;
	items: EnhancedSmartLink[];
};

export type ConnectionItemHandler = (item: EnhancedSmartLink) => void;
export type RefreshLinkUrlHandler = (
	item: EnhancedSmartLink,
) => Promise<EnhancedSmartLink | undefined>;

const styles = cssMap({
	menu: {
		width: MENU_WIDTH,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.medium'),
	},
	twgContent: {
		paddingBlockEnd: token('space.025'),
		maxHeight: TWG_MENU_BODY_MAX_HEIGHT,
		overflowY: 'auto',
	},
	viewMoreContext: {
		paddingInline: token('space.100'),
		paddingBlock: token('space.150'),
	},
});

type MenuShownAttributes = {
	linkCount: number;
	productBreakdown: Record<string, number>;
	authorizedLinkCount: number;
	unauthorizedLinkCount: number;
	connectableLinkCount: number;
};

const getMenuShownAttributes = (groups: ConnectionGroup[] = []): MenuShownAttributes => {
	const attrs: MenuShownAttributes = {
		linkCount: 0,
		productBreakdown: {},
		authorizedLinkCount: 0,
		unauthorizedLinkCount: 0,
		connectableLinkCount: 0,
	};

	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total link count; groups partition a finite link set
	for (const group of groups) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total link count; groups partition a finite link set
		for (const item of group.items) {
			attrs.linkCount += 1;
			attrs.productBreakdown[group.productKey] =
				(attrs.productBreakdown[group.productKey] ?? 0) + 1;
			const isAuthorized = item.access === 'granted';
			if (isAuthorized) {
				attrs.authorizedLinkCount += 1;
			} else {
				attrs.unauthorizedLinkCount += 1;
				if ((item.authProviders?.length ?? 0) > 0) {
					attrs.connectableLinkCount += 1;
				}
			}
		}
	}

	return attrs;
};

const getUnauthenticatedLinkCount = (links: EnhancedSmartLink[] = []): number =>
	links.filter((link) => link.access === 'unauthorized').length;

export type ConnectionsByLineMenuProps = Pick<
	BylineConnectionIconStackProps,
	'additionalIcons' | 'minIcons'
> & {
	/** Shared byline-owned summaries, available for menu features. */
	atAGlance?: UseAtAGlanceResult;
	showFooter?: boolean;
	groups?: ConnectionGroup[];
	links?: EnhancedSmartLink[];
	incomingLinks?: EnhancedSmartLink[];
	relatedContentLinks?: EnhancedSmartLink[];
	devDocsConnection?: DevDocsBylineConnection | null;
	contentId?: string;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onNavigate?: () => void;
	onOpenPreviewPanel: () => void;
};

export const ConnectionsByLineMenu = ({
	showFooter = true,
	groups,
	links,
	additionalIcons,
	minIcons,
	incomingLinks,
	relatedContentLinks,
	devDocsConnection,
	contentId,
	refreshLinkUrl,
	onNavigate,
	onOpenPreviewPanel,
}: ConnectionsByLineMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const menuShownAttributes = useMemo(() => getMenuShownAttributes(groups), [groups]);
	const unauthenticatedLinkCount = useMemo(() => getUnauthenticatedLinkCount(links), [links]);
	const hasFiredRef = useRef(false);
	const hasFiredUnauthenticatedRef = useRef(false);

	const handleViewMoreContext = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectionsBylineViewMoreContext',
				source: 'connectionsBylineMenu',
			},
		}).fire();
		onOpenPreviewPanel();
	}, [createAnalyticsEvent, onOpenPreviewPanel]);

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		hasFiredRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'menu',
				actionSubjectId: 'connectionsBylineMenu',
				source: 'connectionsBylineMenu',
				attributes: menuShownAttributes,
			},
		}).fire();
	}, [createAnalyticsEvent, menuShownAttributes]);

	useEffect(() => {
		if (hasFiredUnauthenticatedRef.current || unauthenticatedLinkCount === 0) {
			return;
		}
		hasFiredUnauthenticatedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'unauthenticatedSmartlink',
				actionSubjectId: 'connectionsBylineMenu',
				source: 'connectionsBylineMenu',
				attributes: { unauthenticatedLinkCount },
			},
		}).fire();
	}, [createAnalyticsEvent, unauthenticatedLinkCount]);

	// The menu shows a flat list of the top links (unconnected first, then document order),
	// matching the "Related content" and "Appears in" sections: the same top-N slice with a
	// "Show more" button that opens the preview panel rather than expanding inline.
	const totalLinkCount = links?.length ?? 0;
	const visibleLinks = useMemo(
		() => getTopConnectionLinks(links ?? [], DEFAULT_VISIBLE_LINK_COUNT),
		[links],
	);
	const hasMoreLinks = totalLinkCount > DEFAULT_VISIBLE_LINK_COUNT;

	const handleShowMoreLinks = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectionsBylineShowMoreLinks',
				source: 'connectionsBylineMenu',
				attributes: { linkCount: totalLinkCount },
			},
		}).fire();
		onOpenPreviewPanel();
	}, [createAnalyticsEvent, onOpenPreviewPanel, totalLinkCount]);

	// 1P references keep the query's recency order (no unconnected-first reordering
	// like "Links to", since these have no "Connect" action).
	const totalReferencedInCount = incomingLinks?.length ?? 0;
	const visibleReferencedIn = useMemo(
		() => (incomingLinks ?? []).slice(0, DEFAULT_VISIBLE_LINK_COUNT),
		[incomingLinks],
	);
	const hasMoreReferences = totalReferencedInCount > DEFAULT_VISIBLE_LINK_COUNT;

	const handleShowMoreReferences = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectionsBylineShowMoreReferences',
				source: 'connectionsBylineMenu',
				attributes: { linkCount: totalReferencedInCount },
			},
		}).fire();
		onOpenPreviewPanel();
	}, [createAnalyticsEvent, onOpenPreviewPanel, totalReferencedInCount]);

	const totalRelatedContentCount = relatedContentLinks?.length ?? 0;
	const visibleRelatedContent = useMemo(
		() => (relatedContentLinks ?? []).slice(0, DEFAULT_VISIBLE_LINK_COUNT),
		[relatedContentLinks],
	);
	const hasMoreRelatedContent = totalRelatedContentCount > DEFAULT_VISIBLE_LINK_COUNT;

	const handleShowMoreRelatedContent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectionsBylineShowMoreRelatedContent',
				source: 'connectionsBylineMenu',
				attributes: { linkCount: totalRelatedContentCount },
			},
		}).fire();
		onOpenPreviewPanel();
	}, [createAnalyticsEvent, onOpenPreviewPanel, totalRelatedContentCount]);

	return (
		<Box xcss={styles.menu} role="dialog" aria-labelledby={HEADER_HEADING_ID}>
			<Stack>
				<ConnectionsByLineMenuHeader />
				{/* Outside `twgContent` so it stays pinned under the header instead of scrolling away
				    with the link sections. */}
				<ConnectionsByLineContextBanner onGenerateSummary={onOpenPreviewPanel} />
				<Box xcss={styles.twgContent} testId="connections-byline-menu.content">
					{devDocsConnection ? (
						<MenuGroup>
							<DevDocsBylineConnectionMenuSection
								connection={devDocsConnection}
								onNavigate={onNavigate}
							/>
						</MenuGroup>
					) : null}
					{/* Every section below is a plain static list of compact rows —
					    `ConnectionsByLineExpandableLinkSection` disables the hover affordances for
					    all of them, so no section reveals a Connect button, a row-action cluster or
					    a hover card. "Show more" opens the preview panel, which is where those
					    actions live. (CCINT-9250) */}
					<ConnectionsByLineExpandableLinkSection
						heading={formatMessage(i18n.relatedContent, { count: totalRelatedContentCount })}
						links={visibleRelatedContent}
						refreshLinkUrl={refreshLinkUrl}
						linkDirection="incoming"
						onNavigate={onNavigate}
						compact
					/>
					{hasMoreRelatedContent ? <ShowMoreButton onClick={handleShowMoreRelatedContent} /> : null}
					<ConnectionsByLineExpandableLinkSection
						heading={formatMessage(i18n.appearsIn, { count: totalReferencedInCount })}
						links={visibleReferencedIn}
						refreshLinkUrl={refreshLinkUrl}
						linkDirection="incoming"
						onNavigate={onNavigate}
						compact
					/>
					{hasMoreReferences ? <ShowMoreButton onClick={handleShowMoreReferences} /> : null}
					<ConnectionsByLineExpandableLinkSection
						heading={formatMessage(i18n.linksTo, { count: totalLinkCount })}
						links={visibleLinks}
						refreshLinkUrl={refreshLinkUrl}
						linkDirection="outgoing"
						onNavigate={onNavigate}
						compact
					/>
					{hasMoreLinks ? <ShowMoreButton onClick={handleShowMoreLinks} /> : null}
					{showFooter ? (
						<ConnectionsByLineMenuFooter links={links ?? []} contentId={contentId} />
					) : null}
					<Box xcss={styles.viewMoreContext}>
						<Button
							appearance="subtle"
							iconBefore={TeamworkGraphIcon}
							iconAfter={ArrowRightIcon}
							shouldFitContainer
							onClick={handleViewMoreContext}
							testId="connections-byline-menu.view-more-context-button"
						>
							<Inline as="span" space="space.100" alignBlock="center">
								<Text weight="regular">{formatMessage(i18n.viewMoreContext)}</Text>
								<BylineConnectionIconStack
									links={links ?? []}
									additionalIcons={additionalIcons}
									minIcons={minIcons}
									spacing="none"
								/>
							</Inline>
						</Button>
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};
